"use client";

import { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { palette, skillColors } from "@/lib/colors";

interface SkillLayer {
  name: string;
  color: string;
}

interface RoleBand {
  name: string;
  start: number;
  end: number;
  color: string;
  skills: string[];
}

const skillLayers: SkillLayer[] = [
  { name: "Performance Media", color: skillColors[0] },
  { name: "Product Growth", color: skillColors[1] },
  { name: "Incrementality", color: skillColors[2] },
  { name: "CRM / Systems", color: skillColors[3] },
  { name: "SEO/GEO", color: skillColors[4] },
  { name: "Partnerships", color: skillColors[5] },
  { name: "Leadership", color: skillColors[6] },
  { name: "Vibecoding", color: skillColors[7] },
];

const roleBands: RoleBand[] = [
  {
    name: "Potloc",
    start: 2016,
    end: 2017,
    color: palette.teal,
    skills: ["Performance Media", "Product Growth"],
  },
  {
    name: "Spark",
    start: 2017,
    end: 2018,
    color: palette.sky,
    skills: ["Performance Media", "Product Growth"],
  },
  {
    name: "Builde",
    start: 2018,
    end: 2021,
    color: palette.amber,
    skills: ["CRM / Systems", "Product Growth", "Vibecoding"],
  },
  {
    name: "Ledger",
    start: 2021,
    end: 2026,
    color: palette.slate,
    skills: [
      "Performance Media",
      "Product Growth",
      "Incrementality",
      "CRM / Systems",
      "SEO/GEO",
      "Partnerships",
      "Leadership",
    ],
  },
];

const generateTimelineData = () => {
  const data = [];
  for (let year = 2016; year <= 2026; year++) {
    const point: any = { year };

    skillLayers.forEach((skill) => {
      let value = 0;

      roleBands.forEach((role) => {
        if (year >= role.start && role.skills.includes(skill.name)) {
          const roleProgress = Math.min(
            (year - role.start) / (role.end - role.start),
            1
          );
          value += 10 + roleProgress * 20;
        }
      });

      point[skill.name] = Math.round(value);
    });

    data.push(point);
  }
  return data;
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const activeRole = roleBands.find(
    (role) =>
      label && parseInt(label) >= role.start && parseInt(label) <= role.end
  );

  return (
    <div className="rounded-lg border border-[--color-border] bg-[--color-bg] p-3 shadow-sm">
      <div className="mb-2 font-mono text-xs font-semibold text-[--color-text]">
        {label}
      </div>
      {activeRole && (
        <div className="mb-2 rounded border border-[--color-border] bg-[--color-bg-tertiary] px-2 py-1 font-mono text-xs font-medium text-[--color-text-secondary]">
          {activeRole.name}
        </div>
      )}
      <div className="space-y-1">
        {payload
          .filter((entry) => entry.value > 0)
          .sort((a, b) => b.value - a.value)
          .map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[--color-text-secondary]">{entry.name}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

export function SkillsTimeline() {
  const [hoveredRole, setHoveredRole] = useState<string | null>(null);
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const timelineData = generateTimelineData();

  return (
    <div className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-[--color-text]">
          Skill Capital Over Time
        </h2>
        <p className="mt-1 text-sm text-[--color-text-secondary]">
          Cumulative expertise acquisition across roles · Hover to explore
        </p>
      </div>

      <div className="rounded-lg border border-[--color-border] bg-[--color-bg] p-6">
        <div className="mb-6">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={timelineData}>
              <defs>
                {skillLayers.map((skill) => (
                  <linearGradient
                    key={skill.name}
                    id={`color${skill.name.replace(/\s|\//g, "")}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={skill.color}
                      stopOpacity={0.45}
                    />
                    <stop
                      offset="95%"
                      stopColor={skill.color}
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={palette.grid}
                vertical={false}
              />
              <XAxis
                dataKey="year"
                stroke={palette.axis}
                style={{
                  fontSize: "12px",
                  fontFamily: "var(--font-family-mono)",
                }}
                domain={[2016, 2026]}
                tickLine={false}
                axisLine={{ stroke: palette.grid }}
              />
              <YAxis stroke={palette.axis} style={{ fontSize: "12px" }} hide />
              <Tooltip content={<CustomTooltip />} />

              <ReferenceLine x={2017} stroke={palette.grid} strokeDasharray="3 3" />
              <ReferenceLine x={2018} stroke={palette.grid} strokeDasharray="3 3" />
              <ReferenceLine x={2021} stroke={palette.grid} strokeDasharray="3 3" />

              {skillLayers.map((skill) => (
                <Area
                  key={skill.name}
                  type="monotone"
                  dataKey={skill.name}
                  stackId="1"
                  stroke={skill.color}
                  strokeWidth={
                    hoveredSkill === skill.name || !hoveredSkill ? 1.5 : 0.5
                  }
                  fill={`url(#color${skill.name.replace(/\s|\//g, "")})`}
                  fillOpacity={
                    hoveredSkill === skill.name || !hoveredSkill ? 1 : 0.25
                  }
                  onMouseEnter={() => setHoveredSkill(skill.name)}
                  onMouseLeave={() => setHoveredSkill(null)}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-3">
          <div className="font-mono text-xs font-semibold uppercase tracking-wider text-[--color-text-muted]">
            Roles
          </div>
          {roleBands.map((role) => {
            const isHovered = hoveredRole === role.name;
            const width = ((role.end - role.start) / 10) * 100;
            const left = ((role.start - 2016) / 10) * 100;

            return (
              <div
                key={role.name}
                className="relative"
                onMouseEnter={() => setHoveredRole(role.name)}
                onMouseLeave={() => setHoveredRole(null)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-32 text-xs font-medium text-[--color-text-secondary]">
                    {role.name}
                  </div>
                  <div className="relative h-8 flex-1">
                    <div
                      className="absolute h-full rounded transition-all"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        backgroundColor: role.color,
                        opacity: isHovered ? 0.18 : 0.1,
                      }}
                    />
                    <div
                      className="absolute inset-y-0 flex items-center gap-1.5 overflow-hidden px-2"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                      }}
                    >
                      {role.skills.map((skill, idx) => {
                        const skillColor =
                          skillLayers.find((s) => s.name === skill)?.color ||
                          "#a3a3a3";
                        return (
                          <div
                            key={idx}
                            className={`rounded border border-[--color-border] bg-[--color-bg-tertiary] px-1.5 py-0.5 font-mono text-[10px] font-medium text-[--color-text-secondary] transition-all ${
                              hoveredSkill === skill || !hoveredSkill
                                ? "opacity-100"
                                : "opacity-30"
                            }`}
                            style={{
                              borderColor: `${skillColor}55`,
                              color: skillColor,
                            }}
                          >
                            {skill.split(" / ")[0]}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="w-20 text-right font-mono text-xs text-[--color-text-muted]">
                    {role.start}–{role.end === 2026 ? "now" : role.end}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-3 border-t border-[--color-border] pt-4">
          {skillLayers.map((skill) => (
            <button
              key={skill.name}
              className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs transition-all ${
                hoveredSkill === skill.name || !hoveredSkill
                  ? "opacity-100"
                  : "opacity-40"
              }`}
              onMouseEnter={() => setHoveredSkill(skill.name)}
              onMouseLeave={() => setHoveredSkill(null)}
            >
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: skill.color }}
              />
              <span className="font-medium text-[--color-text-secondary]">
                {skill.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
